import { Link } from 'react-router';

import { Badge, Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { dataEHoraPorExtenso } from '@/comuns/formato';
import type { Canal } from '@/data/tipos';
import { estiloDaEtiqueta } from '@/comuns/contraste';

/* Cor e ícone são do serviço, não da marca: recolorir o WhatsApp de bordô
   confunde quem opera. */
const SERVICO: Record<Canal['tipo'], { rotulo: string; icone: string; cor: string }> = {
    whatsapp: { rotulo: 'WhatsApp', icone: 'message-circle', cor: '#25D366' },
    instagram: { rotulo: 'Instagram Direct', icone: 'instagram', cor: '#C13584' },
    email: { rotulo: 'E-mail', icone: 'mail', cor: '#2986FF' },
    sms: { rotulo: 'SMS', icone: 'message-square', cor: '#7E8192' },
    webchat: { rotulo: 'Chat do site', icone: 'globe', cor: '#51576B' },
};

const STATUS: Record<Canal['status'], { rotulo: string; tom: 'success' | 'warning' | 'danger' | 'neutral' }> = {
    conectado: { rotulo: 'Conectado', tom: 'success' },
    conectando: { rotulo: 'Conectando', tom: 'warning' },
    erro: { rotulo: 'Com erro', tom: 'danger' },
    desconectado: { rotulo: 'Não conectado', tom: 'neutral' },
};

export interface CanaisModalProps {
    open: boolean;
    canais: Canal[];
    onClose: () => void;
}

export function CanaisModal({ open, canais, onClose }: CanaisModalProps) {
    const algumConectado = canais.some((c) => c.status === 'conectado');
    return (
        <Modal
            open={open} onClose={onClose} width={640} title="Canais de atendimento"
            description="Por onde as conversas entram. Cada clínica conecta os próprios canais."
            footer={<Button variant="secondary" onClick={onClose}>Fechar</Button>}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* O aviso lê o estado dos canais. Era um texto fixo dizendo que o
                    pareamento aguardava autorização — e continuava lá depois de o
                    pareamento por QR existir. */}
                {!algumConectado && (
                    <Alert tone="warning" title="Nenhum canal conectado" dismissible={false}>
                        A resposta escrita aqui fica guardada e só sai depois que um canal for conectado.
                        Conecte em{' '}
                        <Link to="/app/configuracoes/integracoes" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>
                            Configurações › Integrações
                        </Link>
                        .
                    </Alert>
                )}

                {canais.map((c) => {
                    const servico = SERVICO[c.tipo];
                    const status = STATUS[c.status];
                    return (
                        <div
                            key={c.id}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 12, padding: 14,
                                border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
                            }}
                        >
                            <span
                                aria-hidden="true"
                                style={{
                                    width: 34, height: 34, flex: '0 0 auto', borderRadius: 'var(--radius-sm)',
                                    ...estiloDaEtiqueta(servico.cor),
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                <Icon name={servico.icone} size={17} />
                            </span>

                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{c.nome}</p>
                                <p style={{ margin: '2px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                    {servico.rotulo}
                                    {c.numero ? ` · ${c.numero}` : ''}
                                    {c.conversasAbertas ? ` · ${c.conversasAbertas} em aberto` : ''}
                                    {c.ultimaConexaoEm ? ` · conectado em ${dataEHoraPorExtenso(c.ultimaConexaoEm)}` : ''}
                                </p>
                                {c.ultimoErro && (
                                    <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--danger-strong)' }}>{c.ultimoErro}</p>
                                )}
                            </div>

                            <Badge tone={status.tom} dot>{status.rotulo}</Badge>
                        </div>
                    );
                })}
            </div>
        </Modal>
    );
}
